import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import type { Category, CreateProductData, FlowRateUnit } from '@/types/api'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  ArrowLeft,
  DollarSign,
  Package,
  RefreshCw,
  RotateCcw,
  Save,
  Wrench,
  Zap,
} from 'lucide-react'
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'

const productFormSchema = z.object({
  name: z.string().min(1, 'Product name is required'),
  sku: z.string().min(1, 'SKU is required'),
  slug: z.string().optional(),
  description: z.string().min(1, 'Description is required'),
  category_id: z.string().min(1, 'Category is required'),
  brand: z.string().optional(),
  price: z.number().min(0, 'Price must be positive'),
  stock_quantity: z.number().min(0, 'Stock must be positive'),
  production_status: z.enum(['ready', 'pre_order', 'discontinued']),
  is_active: z.boolean(),
  is_featured: z.boolean(),
  lead_time: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  warranty: z.number().min(0, 'Warranty must be a positive number'),
  // Technical specs
  flow_rate: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  flow_rate_unit: z.enum(['m3_h', 'l_min']).default('m3_h'),
  head: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  suction_lift_max: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  pressure_max: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  fluid_temperature: z
    .union([z.number(), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  ambient_temperature: z
    .union([z.number(), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  motor_power: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  voltage_phase: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  rpm: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  motor_efficiency: z
    .union([z.number().min(0).max(100), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  isolation_class: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  ip_protection: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  // Materials
  housing_material: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  impeller_material: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  shaft_material: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  mechanical_seal: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  inlet_outlet_connection: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  product_weight: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  shipping_weight: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  product_dimensions: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  // Additional
  country_of_origin: z.string().optional(),
  use_case: z.string().optional(),
  additional_features: z.array(z.string()).optional(),
  internal_notes: z.string().optional(),
})

type AddProductFormData = z.infer<typeof productFormSchema>

const DEFAULT_FLOW_RATE_UNIT: FlowRateUnit = 'm3_h'
const LITERS_PER_MINUTE_IN_CUBIC_METER_PER_HOUR = 16.6666666667

const roundFlowRateValue = (value: number) => Math.round(value * 100) / 100

const convertFlowRateToCanonical = (value: number, unit: FlowRateUnit) => {
  if (unit === 'l_min') {
    return roundFlowRateValue(value / LITERS_PER_MINUTE_IN_CUBIC_METER_PER_HOUR)
  }

  return roundFlowRateValue(value)
}

const convertFlowRateFromCanonical = (value: number, unit: FlowRateUnit) => {
  if (unit === 'l_min') {
    return roundFlowRateValue(value * LITERS_PER_MINUTE_IN_CUBIC_METER_PER_HOUR)
  }

  return roundFlowRateValue(value)
}

const formatRupiah = (value: number) => {
  return new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(value)
}

const parseRupiahToNumber = (value: string) => {
  const digits = value.replace(/\D/g, '')
  return digits ? Number(digits) : 0
}

const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphen
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
}

const AddProductPage: React.FC = () => {
  const navigate = useNavigate()
  const { toast } = useToast()

  const [categories, setCategories] = useState<Category[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [priceDisplay, setPriceDisplay] = useState('')
  const [uploadedImages, setUploadedImages] = useState<File[]>([])

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    watch,
    setFocus,
  } = useForm<AddProductFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(productFormSchema) as any,
    mode: 'onChange',
    defaultValues: {
      name: '',
      sku: '',
      slug: '',
      description: '',
      category_id: '',
      brand: '',
      price: 0,
      stock_quantity: 0,
      production_status: 'ready',
      is_active: true,
      is_featured: false,
      lead_time: undefined,
      warranty: undefined,
      flow_rate: undefined,
      flow_rate_unit: DEFAULT_FLOW_RATE_UNIT,
      head: undefined,
      suction_lift_max: undefined,
      pressure_max: undefined,
      fluid_temperature: undefined,
      ambient_temperature: undefined,
      motor_power: undefined,
      voltage_phase: '',
      rpm: undefined,
      motor_efficiency: undefined,
      isolation_class: '',
      ip_protection: '',
      housing_material: '',
      impeller_material: '',
      shaft_material: '',
      mechanical_seal: '',
      inlet_outlet_connection: '',
      product_weight: undefined,
      shipping_weight: undefined,
      product_dimensions: '',
      country_of_origin: '',
      use_case: '',
      additional_features: [],
      internal_notes: '',
    },
  })

  const watchedValues = watch()

  useEffect(() => {
    setHasChanges(isDirty)
  }, [isDirty, watchedValues])

  const loadCategories = useCallback(async () => {
    try {
      const response = await apiClient.getCategories({ per_page: 100 })
      setCategories(response.data || [])
    } catch {
      toast({
        title: 'Warning',
        description: 'Failed to load categories. Please refresh the page.',
        variant: 'destructive',
      })
    }
  }, [toast])

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  const onSubmit = async (data: AddProductFormData) => {
    setIsSaving(true)
    try {
      const normalizedFlowRate =
        data.flow_rate === undefined || data.flow_rate === null || isNaN(data.flow_rate)
          ? null
          : convertFlowRateToCanonical(data.flow_rate, data.flow_rate_unit)

      const submitData = {
        ...data,
        lead_time:
          data.lead_time === undefined || data.lead_time === null || isNaN(data.lead_time)
            ? null
            : data.lead_time,
        warranty:
          data.warranty === undefined || data.warranty === null || isNaN(data.warranty)
            ? null
            : data.warranty,
        flow_rate: normalizedFlowRate,
        flow_rate_unit: data.flow_rate_unit,
        head: data.head === undefined || data.head === null || isNaN(data.head) ? null : data.head,
        suction_lift_max:
          data.suction_lift_max === undefined ||
          data.suction_lift_max === null ||
          isNaN(data.suction_lift_max)
            ? null
            : data.suction_lift_max,
        pressure_max:
          data.pressure_max === undefined || data.pressure_max === null || isNaN(data.pressure_max)
            ? null
            : data.pressure_max,
        fluid_temperature:
          data.fluid_temperature === undefined ||
          data.fluid_temperature === null ||
          isNaN(data.fluid_temperature)
            ? null
            : data.fluid_temperature,
        ambient_temperature:
          data.ambient_temperature === undefined ||
          data.ambient_temperature === null ||
          isNaN(data.ambient_temperature)
            ? null
            : data.ambient_temperature,
        motor_power:
          data.motor_power === undefined || data.motor_power === null || isNaN(data.motor_power)
            ? null
            : data.motor_power,
        voltage_phase:
          data.voltage_phase === undefined ||
          data.voltage_phase === null ||
          data.voltage_phase === ''
            ? null
            : data.voltage_phase,
        rpm: data.rpm === undefined || data.rpm === null || isNaN(data.rpm) ? null : data.rpm,
        motor_efficiency:
          data.motor_efficiency === undefined ||
          data.motor_efficiency === null ||
          isNaN(data.motor_efficiency)
            ? null
            : data.motor_efficiency,
        isolation_class:
          data.isolation_class === undefined ||
          data.isolation_class === null ||
          data.isolation_class === ''
            ? null
            : data.isolation_class,
        ip_protection:
          data.ip_protection === undefined ||
          data.ip_protection === null ||
          data.ip_protection === ''
            ? null
            : data.ip_protection,
        housing_material:
          data.housing_material === undefined ||
          data.housing_material === null ||
          data.housing_material === ''
            ? null
            : data.housing_material,
        impeller_material:
          data.impeller_material === undefined ||
          data.impeller_material === null ||
          data.impeller_material === ''
            ? null
            : data.impeller_material,
        shaft_material:
          data.shaft_material === undefined ||
          data.shaft_material === null ||
          data.shaft_material === ''
            ? null
            : data.shaft_material,
        mechanical_seal:
          data.mechanical_seal === undefined ||
          data.mechanical_seal === null ||
          data.mechanical_seal === ''
            ? null
            : data.mechanical_seal,
        inlet_outlet_connection:
          data.inlet_outlet_connection === undefined ||
          data.inlet_outlet_connection === null ||
          data.inlet_outlet_connection === ''
            ? null
            : data.inlet_outlet_connection,
        product_weight:
          data.product_weight === undefined ||
          data.product_weight === null ||
          isNaN(data.product_weight)
            ? null
            : data.product_weight,
        shipping_weight:
          data.shipping_weight === undefined ||
          data.shipping_weight === null ||
          isNaN(data.shipping_weight)
            ? null
            : data.shipping_weight,
        product_dimensions:
          data.product_dimensions === undefined ||
          data.product_dimensions === null ||
          data.product_dimensions === ''
            ? null
            : data.product_dimensions,
        images: uploadedImages,
      }

      await apiClient.createProduct(submitData as unknown as CreateProductData)
      toast({
        title: 'Success',
        description: 'Product created successfully',
      })
      navigate('/products')
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to create product. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const onFormError = () => {
    // Focus on the first invalid field
    if (errors.name) {
      setFocus('name')
    } else if (errors.sku) {
      setFocus('sku')
    } else if (errors.description) {
      setFocus('description')
    } else if (errors.category_id) {
      setFocus('category_id')
    } else if (errors.price) {
      setFocus('price')
    } else if (errors.stock_quantity) {
      setFocus('stock_quantity')
    } else if (errors.warranty) {
      setFocus('warranty')
    }
  }

  const handleReset = () => {
    reset({
      name: '',
      sku: '',
      slug: '',
      description: '',
      category_id: '',
      brand: '',
      price: 0,
      stock_quantity: 0,
      production_status: 'ready',
      is_active: true,
      is_featured: false,
      lead_time: undefined,
      warranty: undefined,
      flow_rate: undefined,
      flow_rate_unit: DEFAULT_FLOW_RATE_UNIT,
      head: undefined,
      suction_lift_max: undefined,
      pressure_max: undefined,
      fluid_temperature: undefined,
      ambient_temperature: undefined,
      motor_power: undefined,
      voltage_phase: '',
      rpm: undefined,
      motor_efficiency: undefined,
      isolation_class: '',
      ip_protection: '',
      housing_material: '',
      impeller_material: '',
      shaft_material: '',
      mechanical_seal: '',
      inlet_outlet_connection: '',
      product_weight: undefined,
      shipping_weight: undefined,
      product_dimensions: '',
      country_of_origin: '',
      use_case: '',
      additional_features: [],
      internal_notes: '',
    })
    setPriceDisplay('')
    setUploadedImages([])
  }

  const handleFlowRateUnitChange = (nextUnit: FlowRateUnit) => {
    const currentUnit = watchedValues.flow_rate_unit ?? DEFAULT_FLOW_RATE_UNIT
    const currentFlowRate = watchedValues.flow_rate

    if (currentFlowRate !== undefined && currentFlowRate !== null && !isNaN(currentFlowRate)) {
      const canonicalFlowRate = convertFlowRateToCanonical(currentFlowRate, currentUnit)
      const convertedFlowRate = convertFlowRateFromCanonical(canonicalFlowRate, nextUnit)

      setValue('flow_rate', convertedFlowRate, { shouldDirty: true, shouldValidate: true })
    }

    setValue('flow_rate_unit', nextUnit, { shouldDirty: true, shouldValidate: true })
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setUploadedImages((prev) => [...prev, ...files])
  }

  const removeImage = (index: number) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index))
  }

  const handleGenerateSlug = async () => {
    const productName = watchedValues.name
    if (!productName) return

    let slug = generateSlug(productName)
    let isUnique = false
    let counter = 1
    const originalSlug = slug

    // Check if slug is unique, if not, append number
    while (!isUnique) {
      try {
        const response = await apiClient.checkSlugAvailability(slug)
        if (response.data.available) {
          isUnique = true
        } else {
          // If slug exists, append counter
          slug = `${originalSlug}-${counter}`
          counter++
        }
      } catch (error) {
        // If error checking slug, just use the generated slug
        console.error('Error checking slug availability:', error)
        isUnique = true
      }
    }

    setValue('slug', slug)
    toast({
      title: 'Success',
      description: 'Slug generated successfully',
    })
  }

  return (
    <div className="space-y-6 p-6 pb-10">
      {/* Header */}
      <div className="space-y-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/products')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Add New Product</h1>
            <p className="text-muted-foreground">
              Create a new product with detailed specifications
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
        {/* Left column: form sections */}
        <form onSubmit={handleSubmit(onSubmit, onFormError)} className="mt-0 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Package className="h-5 w-5 mr-2" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                id="name"
                label="Product Name"
                error={!!errors.name}
                required
                showError={false}
              >
                <Input className="bg-muted/60" {...register('name')} />
              </FormField>
              <FormField id="sku" label="SKU" error={!!errors.sku} required showError={false}>
                <Input className="bg-muted/60" {...register('sku')} />
              </FormField>
              <div>
                <Label htmlFor="slug">Slug</Label>
                <div className="relative">
                  <Input id="slug" className="bg-muted/60 pr-20" {...register('slug')} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleGenerateSlug}
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-muted"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <FormField
                id="category_id"
                label="Category"
                error={!!errors.category_id}
                required
                showError={false}
              >
                <div className="relative">
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-muted/60 px-3 py-2 pr-10 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer"
                    {...register('category_id')}
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={String(cat.id)}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg
                      className="fill-current h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormField>
              <div>
                <Label htmlFor="brand">Brand</Label>
                <Input id="brand" className="bg-muted/60" {...register('brand')} />
              </div>
              <div>
                <Label htmlFor="country_of_origin">Country of Origin</Label>
                <Input
                  id="country_of_origin"
                  className="bg-muted/60"
                  {...register('country_of_origin')}
                />
              </div>
              <FormField
                id="description"
                label="Description"
                error={!!errors.description}
                required
                showError={false}
                className="md:col-span-2"
              >
                <Textarea rows={8} className="bg-muted/60" {...register('description')} />
              </FormField>
              <div>
                <Label htmlFor="use_case">Use Case</Label>
                <Textarea
                  id="use_case"
                  className="bg-muted/60"
                  {...register('use_case')}
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="internal_notes">Internal Notes</Label>
                <Textarea
                  id="internal_notes"
                  {...register('internal_notes')}
                  rows={2}
                  className="bg-muted/60"
                />
              </div>
            </CardContent>
          </Card>

          {/* Pricing & Inventory */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <DollarSign className="h-5 w-5 mr-2" />
                Pricing & Inventory
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField id="price" label="Price" error={!!errors.price} required showError={false}>
                <Controller
                  control={control}
                  name="price"
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className="bg-muted/60"
                      value={priceDisplay}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const numeric = parseRupiahToNumber(event.target.value)
                        field.onChange(numeric)
                        setPriceDisplay(formatRupiah(numeric))
                      }}
                    />
                  )}
                />
              </FormField>
              <FormField
                id="stock_quantity"
                label="Stock Quantity"
                error={!!errors.stock_quantity}
                required
                showError={false}
              >
                <Input
                  type="number"
                  className="bg-muted/60"
                  {...register('stock_quantity', { valueAsNumber: true })}
                />
              </FormField>
              <div>
                <Label htmlFor="production_status">Product Status</Label>
                <div className="relative">
                  <select
                    id="production_status"
                    {...register('production_status')}
                    className="flex h-10 w-full rounded-md border border-input bg-muted/60 px-3 py-2 pr-10 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer"
                  >
                    <option value="ready">Ready</option>
                    <option value="pre_order">Pre-Order</option>
                    <option value="discontinued">Discontinued</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg
                      className="fill-current h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
                {errors.production_status && (
                  <p className="text-sm text-destructive">{errors.production_status.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="lead_time">Lead Time (days)</Label>
                <Input
                  id="lead_time"
                  type="number"
                  className="bg-muted/60"
                  {...register('lead_time', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={watchedValues.is_active}
                    onCheckedChange={(checked) => setValue('is_active', checked)}
                  />
                  <Label htmlFor="is_active" className="mb-0">
                    Active
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_featured"
                    checked={watchedValues.is_featured}
                    onCheckedChange={(checked) => setValue('is_featured', checked)}
                  />
                  <Label htmlFor="is_featured" className="mb-0">
                    Featured
                  </Label>
                </div>
              </div>
              <FormField
                id="warranty"
                label="Warranty (months)"
                error={!!errors.warranty}
                required
                showError={false}
              >
                <Input
                  id="warranty"
                  type="number"
                  className="bg-muted/60"
                  {...register('warranty', { valueAsNumber: true })}
                />
              </FormField>
            </CardContent>
          </Card>

          {/* Technical Specifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Zap className="h-5 w-5 mr-2" />
                Technical Specifications
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="flow_rate">Flow Rate</Label>
                <Input
                  id="flow_rate"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('flow_rate', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="flow_rate_unit">Flow Rate Unit</Label>
                <div className="relative">
                  <select
                    id="flow_rate_unit"
                    value={watchedValues.flow_rate_unit ?? DEFAULT_FLOW_RATE_UNIT}
                    onChange={(event) =>
                      handleFlowRateUnitChange(event.target.value as FlowRateUnit)
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-muted/60 px-3 py-2 pr-10 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer"
                  >
                    <option value="m3_h">m³/h</option>
                    <option value="l_min">L/menit</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg
                      className="fill-current h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="head">Head (m)</Label>
                <Input
                  id="head"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('head', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="suction_lift_max">Max Suction Lift (m)</Label>
                <Input
                  id="suction_lift_max"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('suction_lift_max', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="pressure_max">Max Pressure (bar)</Label>
                <Input
                  id="pressure_max"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('pressure_max', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="fluid_temperature">Fluid Temperature (°C)</Label>
                <Input
                  id="fluid_temperature"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('fluid_temperature', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="ambient_temperature">Ambient Temperature (°C)</Label>
                <Input
                  id="ambient_temperature"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('ambient_temperature', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="motor_power">Motor Power (kW)</Label>
                <Input
                  id="motor_power"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('motor_power', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="voltage_phase">Voltage/Phase</Label>
                <Input
                  id="voltage_phase"
                  className="bg-muted/60"
                  {...register('voltage_phase', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="rpm">RPM</Label>
                <Input
                  id="rpm"
                  type="number"
                  className="bg-muted/60"
                  {...register('rpm', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="motor_efficiency">Motor Efficiency (%)</Label>
                <Input
                  id="motor_efficiency"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('motor_efficiency', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="isolation_class">Isolation Class</Label>
                <Input
                  id="isolation_class"
                  className="bg-muted/60"
                  {...register('isolation_class', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="ip_protection">IP Protection</Label>
                <Input
                  id="ip_protection"
                  className="bg-muted/60"
                  {...register('ip_protection', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Materials & Physical */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Wrench className="h-5 w-5 mr-2" />
                Materials & Physical Properties
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="housing_material">Housing Material</Label>
                <Input
                  id="housing_material"
                  className="bg-muted/60"
                  {...register('housing_material', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="impeller_material">Impeller Material</Label>
                <Input
                  id="impeller_material"
                  className="bg-muted/60"
                  {...register('impeller_material', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="shaft_material">Shaft Material</Label>
                <Input
                  id="shaft_material"
                  className="bg-muted/60"
                  {...register('shaft_material', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="mechanical_seal">Mechanical Seal</Label>
                <Input
                  id="mechanical_seal"
                  className="bg-muted/60"
                  {...register('mechanical_seal', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="inlet_outlet_connection">Inlet/Outlet Connection</Label>
                <Input
                  id="inlet_outlet_connection"
                  className="bg-muted/60"
                  {...register('inlet_outlet_connection', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="product_weight">Product Weight (kg)</Label>
                <Input
                  id="product_weight"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('product_weight', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="shipping_weight">Shipping Weight (kg)</Label>
                <Input
                  id="shipping_weight"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('shipping_weight', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="product_dimensions">Product Dimensions</Label>
                <Input
                  id="product_dimensions"
                  className="bg-muted/60"
                  {...register('product_dimensions', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className="flex items-center justify-between">
            <Button type="button" variant="outline" onClick={handleReset} disabled={!hasChanges}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Form
            </Button>
            <Button type="submit" disabled={!hasChanges || isSaving}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Creating...' : 'Create Product'}
            </Button>
          </div>
        </form>

        {/* Right column: Product Images */}
        <div className="space-y-6">
          {/* Images */}
          <Card>
            <CardHeader>
              <CardTitle>Product Images</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="images"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Upload Images
                  </label>
                  <div className="mt-2">
                    <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400">
                      <div className="text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="mt-2">
                          <p className="text-sm text-gray-600">Click to upload images</p>
                          <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                        </div>
                      </div>
                      <input
                        type="file"
                        id="images"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {uploadedImages.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    {uploadedImages.map((file, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Product image ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default AddProductPage
